import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Loader2, Mic, Search, Trash2, Upload, Video, X } from 'lucide-react';
// TANDA 26b (#235): el `toast` crudo de sonner, el mismo que saca
// `groups.toasts.created` en `GroupsPage`.
// OCR-PROFE-MATERIAL (PR #350, R-1 / #343): desde esta tanda TODOS los avisos de
// esta sección salen por aquí —los rechazos de subida con `toast.error`—, sin
// `id` ni duración propia. «Banner» en el vocabulario de la casa ES la
// notificación de arriba; ningún aviso vive ya como recuadro dentro de la página.
// REMATES-350 (PR #351, R-2 / #346): el «Listo desde tu última visita» que salía
// por `toast.success` se retiró entero (efecto, sello en `localStorage` y llave);
// el único aviso de «ya está listo» es el global del vigilante.
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { readScreenCache, updateScreenCache, writeScreenCache }
  from '../lib/screenDataCache';
import { DOC_DEL_BANCO_SUBIDO } from './GroupBankVigilante';
// REMATES-350 (PR #351, R-4 / #348): la subida de DOCUMENTOS vive a nivel de app en
// `lib/subidasDelBanco.js` (sobrevive a cambiar de pantalla, avisa con avance,
// candado de cierre, una por grupo). Esta sección sólo la arranca y se engancha.
// REBANADAS-BANCO (PR #352): también la GRABACIÓN (`subirAudioAlBanco`, `audioEnVuelo`),
// por el mismo transporte en rebanadas y con su propio carril.
import { audioEnVuelo, subidaEnVuelo, subirAudioAlBanco, subirDocumentoAlBanco, suscribir, textoDelBoton }
  from '../lib/subidasDelBanco';
// TANDA 26 (firma #234): de este módulo ya sólo salen FUNCIONES —la del chip,
// que ahora recibe `t`, y la compuerta de pipeline, que no es texto—. Los copys
// que la card pintaba (`COPYS.documentoError`, `COPYS.sinTemas`) y los dos tags
// que venían del módulo de la curación (`PUBLICACION.tagPublicado`,
// `tagParcialmentePublicado`) viven ahora en `dashboard:groups.bank.doc.*`,
// traducidos, que es lo que arregla la card mitad en inglés y mitad en español
// que un profesor con la app en inglés veía HOY en producción.
import { chipDeDocumento, tienePantallaDeTemas } from '../lib/temasCopys';
// PIPE-INST T5.2 (firma #136): las capas del carril, definidas en un solo sitio.
import {
  CAPA, CAPA_TOCABLE, CAPA_DE_SECCION, CABECERA_DE_SECCION, CHIP_DE_CONTEO, CUERPO_DE_SECCION,
  GUIA_DE_SECCION, TITULO_DE_SECCION_PLEGABLE,
} from '../lib/vestidoDelCarril';
import { useAnchoDeCaja, esAngosto } from '../hooks/useAnchoDeCaja';
// remates-1-3-0c (Q5, W9): la revision por dominio ANTES de mandar la liga (copia de la regla del
// servidor, vigilada por lib/youtubeDelBanco.test.js).
import { esDeOtroSitio } from '../lib/youtubeDelBanco';
import { ordenarIdiomas } from '../lib/ordenDeIdiomas';
import IconoDeMaterial from './IconoDeMaterial';
import { tipoDeMaterial } from '../lib/tipoDeMaterial';
// t7-2b (E2): el chip del audio en fila arma su copia de clases con `cn` (twMerge).
import { cn } from '../lib/utils';

// ajustes-1-3-0 (Bloque F2, ajuste 17): los DOS botones de subida llevan el mismo vestido,
// el del "Import" del consumidor que la firma #301 ya le habia puesto al de audio (y al de
// YouTube); el de documento se suma. Los dos ultimos son de esta tanda: alto libre para
// que la rejilla los estire parejos y texto que puede partirse en dos renglones (a 320 px
// no cabe "Subir audio o video" en una celda de una sola linea).
export const BOTON_DE_SUBIDA = 'bg-[#FF6B00] hover:bg-[#E05A00] active:bg-[#E05A00] active:scale-[0.98] active:duration-0 text-white rounded-full px-4 md:px-6 text-xs md:text-sm shrink-0 min-h-[44px] h-auto whitespace-normal';
// Por debajo de este ancho del renglon de YouTube, el boton dice solo "Agregar" (T16).
export const UMBRAL_YOUTUBE_CORTO = 420;
// remate ajustes-1-3-0 (R3, #510): por debajo de este ancho de la rejilla, los dos botones de
// subida van uno debajo del otro (a todo lo ancho, un renglon cada uno); desde ahi, lado a
// lado. Es el MISMO medidor de la casa (useAnchoDeCaja, ResizeObserver sobre la rejilla): el
// contenedor no es la ventana (en escritorio hay barra lateral), asi que una consulta de medio
// no serviria. Sin medir (0) manda la rejilla de dos columnas, la de siempre.
export const UMBRAL_BOTONES_APILADOS = 480;

/* PILOTO-2 (firma #83) — EL BANCO DEL GRUPO, la sección del profesor.
 *
 * DÓNDE VIVE: dentro de `GroupDetailPage`, o sea ya detrás de
 * `<InstitutionalRoute>` y del `_require_institutional` + `_owned_group_or_404`
 * del servidor. Esta sección NO añade un candado propio: se pinta donde ya no
 * puede llegar nadie más. Un alumno no ve ni un pixel de aquí, y no porque el
 * componente lo esconda — porque la página entera es del dueño.
 *
 * ── LAS TRES DECISIONES DE PANTALLA ─────────────────────────────────────────
 *
 * 1. EL ESTADO SE DICE EN CRISTIANO, NO EN JERGA DE PIPELINE. El profesor no
 *    sabe qué es un `partial`; sabe que «se generó una parte». Los cuatro
 *    estados del backend se traducen uno a uno, y el número de preguntas viaja
 *    tal cual —sin porcentajes de calidad— porque es lo único que hoy podemos
 *    afirmar de verdad sobre el material.
 *
 * 2. LA PROMESA DE B-3 ES FIJA Y ESTÁ A LA VISTA. Cada documento listo lleva
 *    «La revisión llega en la siguiente versión». Sin ella, un profesor con 40
 *    preguntas «listas» razonablemente asumiría que sus alumnos ya las ven — y
 *    en esta tanda NADA se publica. El renglón existe para cerrar esa brecha.
 *
 * 3. EL ERROR DE SUBIDA ES UN AVISO, NO UN TOAST. Los copys C1 y C2 explican
 *    una limitación del producto y prometen una salida (PIPE-INST): son dos
 *    renglones que el profesor necesita LEER y quizá releer, no un mensaje que
 *    se desvanece en cuatro segundos mientras busca otro archivo.
 *
 * ── EL SONDEO, Y POR QUÉ SE APAGA SOLO ──────────────────────────────────────
 *
 * Mientras haya un documento en `processing` se relee la lista cada 5 s; en
 * cuanto ninguno procesa, el intervalo se cancela. El sondeo sólo trae el
 * CAMBIO DE ESTADO.
 *
 * OCR-1 (#314, censo de costuras F.1 #21): AQUÍ VIVÍA LA BARRA DE PROGRESO. Era
 * la del consumidor (`lib/processingProgress`), una curva calibrada para el
 * pipeline del alumno —segundos, con `text_length` como tamaño— pintada sobre un
 * motor que lee documentos completos con visión y tarda lo que tarda. El
 * porcentaje era una promesa que no salía de ningún dato del carril. Se fue con
 * su tic de un segundo; la tarjeta dice el estado real (el chip) y nada más.
 */

const POLL_MS = 5000;
const ESTADOS_EN_VUELO = ['processing'];

/* T6 (firma #286) — EL ESPEJO DE LOS TOPES DEL SERVIDOR.
 *
 * Estos tres valores son una COPIA de `backend/services/group_bank.py`
 * (`EXTENSIONES_AUDIO` y `MAX_AUDIO_MB_BANCO`), y la copia es deliberada: no hay
 * ningún endpoint que los publique y estrenarlo para dos constantes sería una
 * petición más en cada carga de la pantalla. El servidor sigue siendo la verdad —
 * vuelve a validar las dos cosas— y esto sólo existe para no gastarle al profesor
 * varios minutos de subida antes de decirle que su archivo no cabe.
 *
 * Si alguna vez se mueven allá, aquí se aprieta de más (se rechaza algo que el
 * servidor habría aceptado), nunca de menos. */
// post-t7-1 (I2, #563): mkv, mov y webm entran por la misma ruta que el mp4
const EXTENSIONES_AUDIO = ['mp3', 'm4a', 'wav', 'mp4', 'mkv', 'mov', 'webm'];
const MAX_AUDIO_MB = 300;
const MAX_AUDIO_BYTES = MAX_AUDIO_MB * 1024 * 1024;

// AQUÍ VIVÍA `AUDIO_UPLOAD_TIMEOUT_MS` (20 min): el techo del único POST con el que
// la grabación viajaba entera. M-5 (#322) le dio el mismo techo al documento; desde
// REBANADAS-BANCO (PR #352) las dos subidas viajan en rebanadas desde
// `lib/subidasDelBanco.js`. INFRA-1 (#353 b): la rebanada bajó a 8 MiB y el techo
// único se partió en TRES (`TECHO_INACTIVIDAD_MS`, `TECHO_RESPUESTA_MS`,
// `TECHO_REBANADA_MS`), porque un solo número no distingue «va lenta» de «está
// muerta». Los cuatro números viven en el módulo, no aquí.

/** La llave de la foto en memoria de esta lista. Es por grupo. */
const claveDeCache = (groupId) => `pi-bank:${groupId}`;

/* ── LOS TRES CHIPS DEL DOCUMENTO (T4.5) ─────────────────────────────────────
 *
 * Mismo texto de siempre (firma #118) y misma receta de color que los cinco de
 * la pantalla de temas, para que la lista y el detalle se lean como una sola
 * cosa. «Procesando…» lleva el naranja de la marca con el pulso de 2.4 s del
 * mapa de partes: anima OPACIDAD, no layout, así que no re-acomoda la tarjeta. */
const CHIP_BASE = 'inline-flex items-center shrink-0 rounded-full border '
  + 'px-2 py-0.5 text-[11px] leading-4 whitespace-nowrap';
const CHIPS_DOC = {
  processing: 'bg-[#FF6B00]/15 text-[#FF8c3e] border-[#FF6B00]/30 '
    + 'animate-pulse [animation-duration:2.4s]',
  ready: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
  error: 'bg-red-500/15 text-red-400 border-red-500/20',
};

/* t7-2b (E2, pendiente 12 del diseno): EL CHIP DE UN AUDIO EN FILA PUEDE PARTIR RENGLON, Y
 * SOLO ESE. Su texto (el aviso de la fila) es el unico chip largo de la tarjeta, y en un
 * telefono se salia de ella: a 375 px la tarjeta desbordaba 115 px (medido en Chrome con la
 * hoja y el codigo de main). Agregarle al span una clase suelta de "si partir" no basta: la
 * de no partir de CHIP_BASE sale despues en la hoja y gana, y el chip y su contenedor son
 * rigidos, asi que el renglon seguiria midiendo la frase entera. Por eso la copia de clases
 * de ESE chip sale de `cn` (twMerge quita las que chocan) y su contenedor deja de ser rigido
 * SOLO en esa tarjeta: los dos quedan sin crecer, con permiso de encogerse y con su contenido
 * como base (la forma con guion de eso, la que deja pasar el candado A de
 * `perimetroProfe.tanda26.test.js`). En dos renglones el chip lleva el radio de una tarjeta
 * chica; en un renglon se ve igual que la pildora (medido: la captura a 1280 es la misma,
 * byte a byte). CHIP_BASE, los chips de publicado y cualquier otro chip de estado quedan como
 * estaban. */
const CHIP_QUE_PARTE = 'whitespace-normal flex-initial rounded-xl';
const CONTENEDOR_DE_CHIPS = 'ml-auto flex items-center gap-1 shrink-0';
const CONTENEDOR_QUE_CEDE = 'min-w-0 flex-initial';
const esChipDeFila = (doc) => doc?.status === 'processing' && doc?.pi_audio_estado === 'en_fila';

/** El aviso de segunda línea, o cadena vacía si la card no tiene nada que decir.
 *
 *  `pi_sin_temas` lo calcula el servidor con la MISMA compuerta de elegibilidad
 *  que la pantalla de temas (`validacion.es_generable`), así que la lista y el
 *  detalle no pueden discrepar: si aquí dice «no encontramos temas», allá la
 *  lista sale vacía, y al revés. */
function avisoDeCard(t, doc) {
  if (doc?.status === 'error') return t('groups.bank.doc.error');
  if (doc?.status === 'ready' && doc?.pi_sin_temas) return t('groups.bank.doc.no_topics');
  return '';
}

/* ── TANDA 33b — LOS CINCO CÓDIGOS DE T6 QUE HABLABAN INGLÉS ────────────────
 *
 * EL DEFECTO, medido en el censo: el interceptor de `lib/api.js:473-493` traduce
 * el `detail` del servidor en un solo sitio, buscando `common:errors.<error_code>`.
 * Los doce códigos del banco de tandas anteriores tienen esa llave; los CINCO que
 * estrenó T6 no la tienen. Sin llave el interceptor no traduce, `detail` llega
 * crudo, y la función de abajo lo pintaba tal cual. O sea que un profesor con la
 * app en español leía «We can only read MP3, M4A, WAV and MP4 files.»
 *
 * NI UN TEXTO NUEVO NI UN CAMBIO EN EL SERVIDOR HICIERON FALTA. Las cinco frases
 * YA existían en los dos idiomas bajo las llaves de esta misma pantalla — son las
 * que se pintan cuando el fallo lo detecta ella (el pre-chequeo del dispositivo).
 * Lo único que faltaba era nombrarlas desde el código de error.
 *
 * POR QUÉ AQUÍ Y NO EN `common:errors.*`: meterlas al canal global duplicaría el
 * texto en un tercer sitio para decir lo mismo. El mapa vive junto a la pantalla
 * que lo usa, y `profeBilingue.tanda26.test.js` lo lee para exigir que TODO
 * `error_code` del banco se pueda decir en los dos idiomas por una vía o por la
 * otra — que es el candado que no existía y por eso esto se coló. */
// remates-1-3-0c (Q5, W9): los avisos del campo de YouTube son los del alumno, con sus textos
// copiados (y vigilados, GroupBankSection.youtubeQ5.test.js): la liga de otro sitio (Y1, codigo
// nuevo del servidor del banco), y el video que no se pudo leer (Y2) para el 422 y el 503, que
// decian dos frases distintas (`no_transcript` y `unavailable`, retiradas).
export const LLAVE_POR_CODIGO = {
  pi_youtube_invalid_url: 'groups.bank.youtube.invalid_url',
  pi_youtube_not_youtube: 'groups.bank.youtube.no_es_youtube',
  pi_youtube_no_transcript: 'groups.bank.youtube.no_se_pudo_leer',
  pi_youtube_unavailable: 'groups.bank.youtube.no_se_pudo_leer',
  pi_audio_bad_format: 'groups.bank.audio.bad_format',
  pi_audio_too_large: 'groups.bank.audio.too_large',
};

// El `detail` del servidor puede venir como cadena (los copys C1-C4) o como
// objeto (el sobre estructurado del PDF con contraseña, que ya existía en el
// consumidor). Se normaliza a texto para el aviso, sin inventar copy propio: si
// el servidor no dijo nada legible, la pantalla cae a su frase genérica.
//
// TANDA 33b: el código hermano MANDA sobre el texto. Se mira primero porque es
// lo único estable — el inglés del servidor puede reescribirse cualquier día y
// el código no. `mb` viaja siempre: sólo lo usa la frase del tope de audio, y el
// número sale de la constante de esta casa (la misma que usa el pre-chequeo), no
// de rascar el inglés.
/* INFRA-1 (censo C5) — LA TARJETA DOBLE, Y POR QUÉ EL DEDUPE VA EN UN HELPER.
 *
 * EL BUG, VISTO EN EL SMOKE DE #352: `chico_352.txt` apareció DOS VECES en la
 * lista durante un segundo. No era un doble render: eran dos caminos escribiendo
 * la misma tarjeta con un instante de diferencia. El módulo de subida avisa
 * `listo` con el documento en la mano, y el sondeo de la lista —que corre cada
 * 5 s mientras algo procesa— trae ese mismo documento del servidor. Los dos
 * anteponen; ninguno preguntaba si ya estaba.
 *
 * SE ARREGLA EN UN HELPER Y NO EN CADA SITIO porque son CUATRO (audio, documento,
 * YouTube y el cache), y un dedupe que hay que acordarse de copiar es un dedupe
 * que el quinto camino no va a tener. GANA LA PRIMERA APARICIÓN: la que ya está
 * en la lista puede traer campos que el evento no (`question_count` tras un
 * refresco), y sustituirla sería perderlos.
 *
 * Y NO ES SÓLO COSMÉTICO: el contador de «banco lleno» cuenta filas de la lista
 * (`lista.filter(...)`), así que una tarjeta doble se come un cupo de los 25 del
 * grupo mientras dure. */
export function anteponerSinRepetir(lista, doc) {
  const previa = lista || [];
  if (!doc || !doc.id) return previa.slice();
  if (previa.some((x) => x && x.id === doc.id)) return previa.slice();
  return [doc, ...previa];
}

export function textoDeFalloDeSubida(err, t) {
  const llave = LLAVE_POR_CODIGO[err?.response?.data?.error_code];
  if (llave) return t(llave, { mb: MAX_AUDIO_MB });
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (detail && typeof detail === 'object' && typeof detail.message === 'string') return detail.message;
  return t('groups.bank.upload_failed');
}

/* OCR-PROFE-MATERIAL (PR #350, R-1 / #343) — AQUÍ VIVÍA LA FRANJA DEL BANCO.
 *
 * `BannerDelBanco` (P-2 de #349) pintaba dentro de la página los rechazos de subida
 * en ámbar y el «Listo desde tu última visita» en verde, cada uno con su «Cerrar».
 * Se retiró entera: la decisión firmada el 7-sep dice que «banner» es la
 * notificación de arriba y que ningún aviso vive como recuadro. Los 16 mensajes del
 * censo salen ahora por `toast.error` (ver `avisar`) y el verde por `toast.success`
 * (ver el efecto de la visita), con los MISMOS textos y las MISMAS llaves. El
 * mensaje 5 («varios idiomas») sigue siendo el modal de abajo. */

/* P-2.3 — EL MODAL DE IDIOMA DEL BANCO. Réplica del que abre el Panel del alumno
 * cuando un video de YouTube trae subtítulos en varios idiomas (`Dashboard.js`,
 * `youtube-lang-modal`): misma estructura, mismos textos copiados verbatim a llaves
 * del profe (`groups.bank.youtube.choose_language_*`), y código propio del carril —
 * el banco y el Panel son tuberías separadas y no comparten piezas. El nombre de cada
 * idioma sale de `Intl.DisplayNames` en el idioma de la app; el código, en crudo. */
function nombreDeIdioma(codigo, idiomaApp) {
  try {
    const dn = new Intl.DisplayNames([idiomaApp || 'en'], { type: 'language' });
    return dn.of(codigo) || codigo;
  } catch {
    return codigo;
  }
}

/* t7-2a (C4) -- AQUI VIVIA `tipoDeMaterial`. Desde el remate 2 (#529) lo decide
   `lib/tipoDeMaterial.js`, el mismo que usa el Panel del alumno (primero `file_type`, que el
   servidor escribe al CREAR el documento: el icono es el correcto desde que la tarjeta
   aparece, tambien mientras procesa). */

/* t7-2a (C4) -- la linea bajo el nombre: "N temas . M cards publicadas" (sin cards, solo los
   temas); mientras procesa, el texto de estado que ya existe; en los demas casos, nada (el
   aviso de la tarjeta ya explica el error o la falta de temas). */
export function lineaDelMaterial(t, doc) {
  if (doc?.status === 'processing') return t('groups.bank.doc.chip_processing');
  const temas = doc?.pi_temas_n || 0;
  if (doc?.status !== 'ready' || temas === 0) return null;
  const partes = [t('doc_card.topic_count', { count: temas })];
  if ((doc?.pi_cards_n || 0) > 0) partes.push(t('groups.avance.cards_publicadas', { count: doc.pi_cards_n }));
  return partes.join(', ');
}

export function GroupBankIdiomaModal({ idiomas, cargando, onElegir, onCerrar }) {
  const { t, i18n } = useTranslation(['dashboard', 'common']);
  const [busqueda, setBusqueda] = useState('');
  const filtro = busqueda.trim().toLowerCase();
  // post-t7-1 (I1, #562): el mismo orden que la ventana del alumno (una sola funcion)
  const opciones = ordenarIdiomas(idiomas)
    .map((code) => ({ code, name: nombreDeIdioma(code, i18n?.language) }))
    .filter(({ code, name }) => !filtro || code.toLowerCase().includes(filtro) || (name || '').toLowerCase().includes(filtro));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="group-bank-idioma-modal">
      <div className="bg-[#111] border border-[#222] rounded-2xl p-6 w-full max-w-[420px] mx-4 shadow-2xl relative">
        <button type="button" onClick={onCerrar}
          className="modal-close-tap absolute top-4 right-4 text-[#555] hover:text-white transition"
          aria-label={t('common:a11y.close')}
          data-testid="group-bank-idioma-cerrar">
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-[#FF6B00]/10 flex items-center justify-center shrink-0">
            <Video className="w-5 h-5 text-[#FF6B00]" />
          </div>
          <h3 className="text-lg font-semibold text-white pr-6">{t('groups.bank.youtube.choose_language_title')}</h3>
        </div>
        <p className="text-sm text-[#888] mb-4">{t('groups.bank.youtube.choose_language_desc')}</p>
        {(idiomas || []).length > 8 && (
          <div className="relative mb-3">
            <Search className="w-4 h-4 text-[#555] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
              placeholder={t('groups.bank.youtube.choose_language_search')}
              className="w-full h-9 rounded-md bg-[#0a0a0a] border border-[#262626] pl-9 pr-3 text-sm text-white placeholder:text-[#555] outline-none focus:border-[#FF6B00]"
              data-testid="group-bank-idioma-buscar" />
          </div>
        )}
        <div className="max-h-[260px] overflow-y-auto space-y-1 pr-1">
          {opciones.map(({ code, name }) => (
            <button key={code} type="button" disabled={cargando}
              onClick={() => onElegir(code)}
              className="w-full text-left px-3 py-2 rounded-lg text-sm text-[#ccc] hover:bg-[#1f1f1f] hover:text-white transition flex items-center justify-between disabled:opacity-50"
              data-testid={`group-bank-idioma-${code}`}>
              <span>{name}</span>
              <span className="text-xs text-[#555] uppercase">{code}</span>
            </button>
          ))}
        </div>
        {cargando && (
          <div className="flex items-center justify-center gap-2 text-xs text-[#888] mt-3">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> {t('groups.bank.youtube.loading')}
          </div>
        )}
      </div>
    </div>
  );
}

export default function GroupBankSection({ groupId }) {
  const { t } = useTranslation(['dashboard', 'common']);
  const navigate = useNavigate();
  // ajustes-1-3-0 (Bloque F2): el renglon de YouTube se mide para acortar su boton.
  const renglonDeYoutube = useRef(null);
  const anchoDelRenglon = useAnchoDeCaja(renglonDeYoutube);
  // R3 (#510): la rejilla de los dos botones se mide para apilarlos en telefono.
  const rejillaDeBotones = useRef(null);
  const anchoDeLaRejilla = useAnchoDeCaja(rejillaDeBotones);
  const botonesApilados = esAngosto(anchoDeLaRejilla, UMBRAL_BOTONES_APILADOS);

  // LA LISTA ARRANCA DE LA MEMORIA CUANDO LA HAY (T4.5). Volver al grupo desde
  // la pantalla de temas es el viaje MÁS FRECUENTE del profesor, y hasta aquí
  // costaba un segundo en blanco cada vez. Se pinta lo último bueno al instante
  // y la relectura de abajo lo actualiza por detrás — que es lo mismo que esta
  // lista ya hace cada 5 s mientras algo procesa. Se usa el cache del producto y
  // no un Map propio para heredar su purga por frontera de identidad.
  const [docs, setDocs] = useState(
    () => readScreenCache(claveDeCache(groupId))?.data?.documents || null);
  const [maxDocs, setMaxDocs] = useState(
    () => readScreenCache(claveDeCache(groupId))?.data?.max_documents ?? null);
  // `subiendo` es la grabación y el YouTube. La subida de documento es `subidaDoc`:
  // `{ nombre, pct }` mientras el módulo la tenga en vuelo.
  // REBANADAS-BANCO (PR #352, cabo de audio): la grabación también vive en el módulo
  // (`subirAudioAlBanco`, mismo transporte por rebanadas, su propio carril); al montar
  // se re-engancha igual que el documento. Lo que ve el profesor no cambia: el botón
  // apagado con su espera, la tarjeta al aterrizar y el aviso de fallo de siempre.
  const [subiendo, setSubiendo] = useState(() => !!audioEnVuelo(groupId));
  const [subidaDoc, setSubidaDoc] = useState(() => subidaEnVuelo(groupId));
  // INFRA-1 (#353 a): el AVANCE de la grabación. `subiendo` sigue siendo el
  // booleano que apaga los tres botones (lo comparte con YouTube, que no tiene
  // avance ninguno); `subidaAudio` es `{ nombre, pct }` mientras el módulo tenga
  // la grabación en vuelo, exactamente como `subidaDoc` para el documento.
  const [subidaAudio, setSubidaAudio] = useState(() => audioEnVuelo(groupId));
  const [trabajando, setTrabajando] = useState({});   // {docId: true} reintento/borrado
  const [confirmarBorrado, setConfirmarBorrado] = useState(null);
  // t7-2a (C2): la seccion "Banco del grupo" llega ABIERTA; el estado no se recuerda.
  const [bancoAbierto, setBancoAbierto] = useState(true);

  const inputRef = useRef(null);
  const audioRef = useRef(null);
  const [urlYoutube, setUrlYoutube] = useState('');
  // P-2.3: {url, langs} mientras el profesor elige el idioma del video; null si no.
  const [modalIdioma, setModalIdioma] = useState(null);

  /* R-1 (#343) — EL ÚNICO CAMINO DE UN AVISO DE SUBIDA. Los siete disparadores de
     siempre (los dos pre-chequeos del audio, los tres `catch` de las subidas, el
     `catch` del borrado) llegan aquí con el texto ya resuelto —el mismo que pintaba
     el recuadro— y sale como el toast de error de la app, sin `id` ni duración. */
  const avisar = (texto) => { toast.error(texto); };

  // Las dos mitades del write-through. `updateScreenCache` sólo actúa si YA hay
  // entrada, así que antes de la primera lectura buena no inventan ninguna.
  const quitarDelCache = useCallback((docId) => {
    updateScreenCache(claveDeCache(groupId), (d) => ({
      ...d, documents: (d?.documents || []).filter(x => x.id !== docId) }));
  }, [groupId]);
  const anadirAlCache = useCallback((doc) => {
    updateScreenCache(claveDeCache(groupId), (d) => ({
      ...d, documents: anteponerSinRepetir(d?.documents, doc) }));
  }, [groupId]);

  const cargar = useCallback(async () => {
    try {
      const res = await api.get(`/groups/${groupId}/bank`);
      setDocs(res.data.documents || []);
      if (typeof res.data.max_documents === 'number') setMaxDocs(res.data.max_documents);
      writeScreenCache(claveDeCache(groupId), res.data);
    } catch (err) {
      // Una lectura fallida NO borra lo que ya está en pantalla: se conserva la
      // última foto buena. Con la lista todavía en null se pinta la vacía, que
      // es honesta («no hay nada que mostrar») y tiene su botón de subir.
      setDocs(prev => (prev === null ? [] : prev));
    }
  }, [groupId]);

  useEffect(() => { cargar(); }, [cargar]);

  /* R-4 (#348) — EL RE-ENGANCHE. Al montar (o al cambiar de grupo) se mira si el
     módulo tiene una subida en vuelo para ESTE grupo y se escucha su avance: el
     botón pinta el porcentaje real; al terminar, la tarjeta «Procesando…» aparece
     aquí mismo (con el documento que devolvió el servidor, o recargando); al
     fallar, el botón vuelve a su estado normal (el toast de error lo dio el
     módulo, una vez). Sin subida en vuelo, no pasa nada. El vigilante global lo
     despierta el módulo, esté o no montada esta sección. */
  useEffect(() => {
    setSubidaDoc(subidaEnVuelo(groupId));
    const audio = audioEnVuelo(groupId);
    setSubidaAudio(audio);
    setSubiendo(!!audio);
    return suscribir(groupId, (ev) => {
      // INFRA-1 (#353 a): el carril del audio ya lleva avance y toast propios (los
      // pinta el módulo, con las llaves de #348). Esta sección consume el `pct`
      // para el botón —igual que el documento— y ya NO avisa el fallo: lo avisa el
      // módulo, y hacerlo aquí también sería el mismo toast dos veces.
      if (ev.tipo === 'audio') {
        if (ev.estado === 'subiendo') {
          setSubidaAudio({ nombre: ev.nombre, pct: ev.pct });
          return;
        }
        setSubidaAudio(null);
        setSubiendo(false);
        if (ev.estado === 'listo') {
          if (ev.documento) {
            setDocs(prev => anteponerSinRepetir(prev, ev.documento));
            anadirAlCache(ev.documento);
          } else cargar();
        }
        return;
      }
      if (ev.estado === 'subiendo') { setSubidaDoc({ nombre: ev.nombre, pct: ev.pct }); return; }
      setSubidaDoc(null);
      if (ev.estado !== 'listo') return;
      if (ev.documento) {
        setDocs(prev => anteponerSinRepetir(prev, ev.documento));
        anadirAlCache(ev.documento);
      } else cargar();
    });
  }, [groupId, cargar, anadirAlCache, t]);

  const hayEnVuelo = (docs || []).some(d => ESTADOS_EN_VUELO.includes(d.status));

  // EL SONDEO. Depende de `hayEnVuelo`, así que el intervalo NACE cuando algo
  // empieza a procesar y MUERE cuando nada procesa — no hay un `clearInterval`
  // condicional escondido en un callback que pueda quedarse colgado.
  useEffect(() => {
    if (!hayEnVuelo) return undefined;
    const id = setInterval(cargar, POLL_MS);
    return () => clearInterval(id);
  }, [hayEnVuelo, cargar]);

  const elegirArchivo = () => { inputRef.current?.click(); };
  const elegirAudio = () => { audioRef.current?.click(); };

  /* T6 (firma #286) — LO QUE UNA SUBIDA HACE AL ATERRIZAR: pintar la tarjeta al
     instante, escribirla al cache, y despertar al vigilante global para que el
     aviso de «ya está listo» alcance al profesor esté donde esté. Olvidar el
     último es el bug que la T4.5 existió para cerrar.

     INFRA-1 (censo C5, corrección de cita): el comentario decía «LO QUE LAS TRES
     SUBIDAS COMPARTEN» y hoy sólo lo llama YouTube. El documento y la grabación
     salieron a `suscribir` en #348 y en #352, y el módulo ya despierta al
     vigilante por los dos: copiar este camino al audio dispararía el evento dos
     veces. */
  const aterrizar = (nuevo) => {
    if (nuevo) { setDocs(prev => anteponerSinRepetir(prev, nuevo)); anadirAlCache(nuevo); }
    else cargar();
    window.dispatchEvent(new Event(DOC_DEL_BANCO_SUBIDO));
  };

  /* EL PRE-CHEQUEO EN EL DISPOSITIVO, Y POR QUÉ EXISTE.
     Subir 400 MB para que el servidor conteste «demasiado grande» es gastarle al
     profesor varios minutos de su conexión para decirle algo que su teléfono ya
     sabía. Los dos rechazos se miran ANTES de abrir una petición: extensión y
     tamaño. El servidor los vuelve a mirar —un candado que se salta con `curl` no
     es un candado— pero aquí no cuesta ni un byte de red. */
  const subirAudio = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const ext = file.name.includes('.') ? file.name.split('.').pop().toLowerCase() : '';
    if (!EXTENSIONES_AUDIO.includes(ext)) {
      avisar(t('groups.bank.audio.bad_format'));
      return;
    }
    if (file.size > MAX_AUDIO_BYTES) {
      avisar(t('groups.bank.audio.too_large', { mb: MAX_AUDIO_MB }));
      return;
    }
    /* REBANADAS-BANCO (PR #352, cabo de audio) — LA GRABACIÓN YA NO SE MANDA DESDE
       AQUÍ. Hasta esta tanda salía en UN `POST /bank/upload-audio` con el techo de 20
       min (`AUDIO_UPLOAD_TIMEOUT_MS`), y Cloudflare corta cualquier cuerpo de más de
       100 MB antes de llegar a Render. Ahora la arranca el módulo
       (`subirAudioAlBanco`: rebanadas de 8 MiB con SHA-256, mismas garantías que el
       documento, `tipo: 'audio'`, su propio carril) y esta sección se entera por
       `suscribir` (arriba): al terminar aterriza la tarjeta.

       INFRA-1 (#353 a): y AHORA con el mismo aviso que el documento. El botón dice
       «Subiendo… N %» y el módulo pinta el toast persistente, el éxito y el fallo
       — por eso esta sección ya NO avisa el fallo del audio: sería el mismo toast
       dos veces. Todo con las llaves de #348: cero llaves nuevas. */
    const r = subirAudioAlBanco({
      groupId, file, t, textoDeFallo: (err) => textoDeFalloDeSubida(err, t),
    });
    // SE SIEMBRA EL ESTADO EN CUANTO SE ELIGE EL ARCHIVO, igual que el documento
    // (`subir`, más arriba). Sin esta línea el botón se queda con su texto normal
    // hasta que llega el primer porcentaje honesto — y en un archivo grande eso es
    // el tiempo que tarda en hashearse la primera rebanada, que es justo cuando el
    // profesor está mirando a ver si pasó algo. `pct: null` es un estado de primera
    // clase: el botón dice «Subiendo…» sin inventarse un número.
    if (!r.ocupado) {
      setSubidaAudio({ nombre: file.name, pct: null });
      setSubiendo(true);
    }
  };

  const subirYoutube = async (urlElegida, lang) => {
    const url = (urlElegida || urlYoutube).trim();
    if (!url) return;
    // remates-1-3-0c (Q5, W9): Y1, como al alumno, y ANTES de mandar nada: una liga de otro sitio
    // no viaja (el servidor del banco diria lo mismo con `pi_youtube_not_youtube`).
    if (esDeOtroSitio(url)) {
      toast.error(t('groups.bank.youtube.no_es_youtube'));
      return;
    }
    setSubiendo(true);
    try {
      // P-2.3 (PR #349, #326): el reintento con el idioma elegido lleva `lang`, el
      // MISMO parámetro que manda el Panel del alumno y que `routes/group_bank.py`
      // ya lee (`data.get("lang")`).
      const res = await api.post(`/groups/${groupId}/bank/from-youtube`, { url, ...(lang ? { lang } : {}) });
      // `choose_language` NO ES UN ERROR: el video tiene subtítulos en varios
      // idiomas y el servidor pide que se elija uno. Desde P-2.3 la pantalla del
      // profesor abre su propio modal de idioma (réplica del del alumno); elegir
      // repite esta misma subida con `lang`, cancelar no sube nada.
      if (res.data?.action === 'choose_language') {
        setModalIdioma({ url, langs: res.data.available_langs || [] });
        return;
      }
      setModalIdioma(null);
      setUrlYoutube('');
      aterrizar(res.data?.document);
    } catch (err) {
      setModalIdioma(null);
      /* TANDA 33b (firma #304) — LA LIGA INVÁLIDA SE AVISA COMO AL ALUMNO.
         Mismo mecanismo (el toast rojo de la casa, `toast.error` sin `id` ni
         duración propia — igual que `Dashboard.js:1540`) y mismas palabras,
         duplicadas en la llave del profesor. Se duplican y no se comparten: son
         dos tuberías con dueños distintos, y el día que el Panel del alumno
         retoque su copy el profesor no debe moverse solo. `GroupBankSection.
         bilingue.test.js` lee los dos JSON y compara carácter por carácter, así
         que la copia queda vigilada.

         R-1 (#343): desde esta tanda los demás avisos también son toast, así que
         la rama de abajo ya no es la excepción; se conserva aparte porque su
         texto sale de la llave directa y no del `detail` del servidor. */
      if (err?.response?.data?.error_code === 'pi_youtube_invalid_url') {
        toast.error(t('groups.bank.youtube.invalid_url'));
        return;
      }
      // remates-1-3-0c (Q5, W9): el duplicado de una liga dice lo del alumno ("Ya tienes este
      // documento."), en el banner informativo, como el alumno; decia el aviso rojo de un ARCHIVO
      // repetido ("Ya subiste este mismo archivo a este grupo."), que sigue para los archivos.
      if (err?.response?.data?.error_code === 'bank_duplicate_in_group') {
        toast.info(t('groups.bank.youtube.duplicado'));
        return;
      }
      avisar(textoDeFalloDeSubida(err, t));
    } finally { setSubiendo(false); }
  };

  /* R-4 (#348) — LA SUBIDA YA NO VIVE AQUÍ. Hasta esta tanda `subir` hacía el
     `api.post` con `DOC_UPLOAD_TIMEOUT_MS`, pintaba la tarjeta, despertaba al
     vigilante y avisaba el fallo, todo desde el estado del componente: cambiar de
     pantalla perdía el resultado y el botón no decía cuánto faltaba. Ahora lo
     arranca el módulo (`subirDocumentoAlBanco`, mismo techo de 20 min, mismo
     resolutor de texto de fallo) y esta sección se entera por `suscribir` (arriba).
     Si ya hay una subida en vuelo para este grupo, el módulo no manda nada y avisa. */
  const subir = (e) => {
    const file = e.target.files?.[0];
    // El input se limpia SIEMPRE, gane o pierda: sin eso, volver a elegir el
    // MISMO archivo tras un rechazo no dispara `change` y el botón queda mudo.
    e.target.value = '';
    if (!file) return;
    const r = subirDocumentoAlBanco({
      groupId, file, t, textoDeFallo: (err) => textoDeFalloDeSubida(err, t),
    });
    if (!r.ocupado) setSubidaDoc({ nombre: file.name, pct: null });
  };

  // EL BOTÓN DE «REINTENTAR» SE FUE EN PIPE-INST T4, y no es cosmética.
  //
  // Sus dos ramas del servidor agendan el pipeline del CONSUMIDOR; sobre un
  // documento del carril institucional eso significaría meter material de terra
  // por la tubería del consumidor (hoy gpt-5.6-luna), borrando de paso su mapa y
  // sus preguntas. El servidor ya lo niega (409, `routes/group_bank.py`), y la
  // pantalla deja de ofrecer una salida que no existe: un documento que murió
  // leyéndose se vuelve a subir (copy 3), y un TEMA que no se pudo generar se
  // vuelve a palomear en su pantalla. No hay un tercer caso que atender.

  const borrar = async () => {
    const docId = confirmarBorrado?.id;
    if (!docId) return;
    setTrabajando(p => ({ ...p, [docId]: true }));
    try {
      await api.delete(`/groups/${groupId}/bank/${docId}`);
      setDocs(prev => (prev || []).filter(d => d.id !== docId));
      // LA MUTACION VA TAMBIEN AL CACHE (write-through), como en el resto del
      // producto. Sin esto, el borrado sólo tocaba el estado del componente y la
      // foto en memoria conservaba la lista de ANTES: al volver del viaje más
      // frecuente del profesor —entrar a los temas y regresar— el documento
      // borrado REAPARECIA, con su nombre navegable a un 404 y su bote de basura
      // llamando a un DELETE que ya no existe.
      quitarDelCache(docId);
      setConfirmarBorrado(null);
      // TANDA 26b (#235) — EL BORRADO SE ACUSA. La card desaparece, sí, pero
      // «desapareció» es lo que también parece cuando la lista se recarga sola o
      // cuando el sondeo la reordena. Un toast lo dice sin ambigüedad, con el
      // mismo `toast` crudo de sonner que ya usa el resto del carril y con su
      // duración por defecto: es un acuse, no un texto que haya que leer dos
      // veces (ésos son los del recuadro de subida, que se quedan).
      toast.success(t('groups.bank.doc.deleted_toast'));
    } catch (err) {
      avisar(textoDeFalloDeSubida(err, t));
      setConfirmarBorrado(null);
    } finally { setTrabajando(p => ({ ...p, [docId]: false })); }
  };

  const lista = docs || [];
  const lleno = typeof maxDocs === 'number' && lista.filter(d => d.status !== 'error').length >= maxDocs;

  return (
    /* PIPE-INST T5.1 (firma #132, §4.2) — FUERA EL MARCO GIGANTE. El título de
       la sección pasa a ser texto sobre el fondo y las cards de DOCUMENTO se
       quedan: ellas sí son cards, y con el marco de fuera retirado por fin se
       leen como la unidad que son. El `data-testid` no se mueve —lo miran dos
       pruebas de PILOTO-2— y tampoco se mueve un solo handler. */
    <>
    {/* t7-2a (C2, leccion 120): el banco vive en la capa de seccion del profe, ABIERTO al
        llegar, con el conteo de materiales en su chip. Adentro, todo lo de antes sin cambio; el
        cuerpo se esconde sin desmontarse (la subida en vuelo no se pierde al plegar). */}
    <div className={CAPA_DE_SECCION} data-testid="group-bank-section">
      <h2 className={TITULO_DE_SECCION_PLEGABLE} data-testid="group-bank-titulo">
        <button type="button" onClick={() => setBancoAbierto((v) => !v)} aria-expanded={bancoAbierto}
          className={CABECERA_DE_SECCION} data-testid="group-bank-plegar">
          <span className={GUIA_DE_SECCION} aria-hidden="true" />
          <span className="flex-1 min-w-0 truncate" data-testid="group-bank-plegar-titulo">{t('groups.bank.title')}</span>
          <span className={CHIP_DE_CONTEO} data-testid="group-bank-conteo">
            {t('groups.avance.materiales', { count: lista.length })}
          </span>
          <ChevronDown aria-hidden="true"
            className={`w-4 h-4 text-[#8c8c8c] shrink-0 transition-transform ${bancoAbierto ? 'rotate-180' : ''}`} />
        </button>
      </h2>
      <div hidden={!bancoAbierto} className={CUERPO_DE_SECCION} data-testid="group-bank-cuerpo">
      {/* ajustes-1-3-0 (Bloque F2, ajuste 17): los dos botones de subida en una rejilla de dos
          columnas iguales (mismo alto, mismo vestido), debajo del titulo; los dos inputs
          escondidos siguen a su lado y no ocupan celda. */}
      <div ref={rejillaDeBotones} className={botonesApilados ? 'grid grid-cols-1 gap-3' : 'grid grid-cols-2 gap-3'}
        data-botones-apilados={botonesApilados ? '1' : undefined} data-testid="group-bank-botones">
        {/* R-4 (#348): mientras sube, el botón dice «Subiendo…» y, en cuanto hay
            un porcentaje honesto, «Subiendo… N %». Los dos botones de subida se
            apagan con cualquiera de las dos subidas en vuelo. */}
        <Button onClick={elegirArchivo} disabled={subiendo || !!subidaDoc || lleno}
          className={BOTON_DE_SUBIDA}
          data-testid="group-bank-upload-btn">
          {(subiendo || subidaDoc)
            ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            : <Upload className="w-3.5 h-3.5 mr-2" />}
          {subidaDoc ? textoDelBoton(t, subidaDoc.pct) : t('groups.bank.upload_btn')}
        </Button>
        {/* El input vive fuera del flujo visual: el botón de arriba es el que se
            ve y el que mide 44 pt. `accept` es una sugerencia del explorador de
            archivos, no un candado — el candado real está en el servidor. */}
        <input ref={inputRef} type="file" className="hidden"
          accept=".pdf,.docx,.doc,.pptx,.ppt,.txt" onChange={subir}
          data-testid="group-bank-file-input" />

        {/* T6 (firma #286) — LA GRABACIÓN, HERMANA DEL BOTÓN DE ARRIBA.
            Mismo alto, mismo sitio, misma mecánica de input escondido.

            TANDA 33 (firma #301) — Y AHORA TAMBIÉN MISMO VESTIDO. T6 la puso en
            secundario (borde, sin relleno) razonando que subir un documento sigue
            siendo lo que un profesor hace nueve de cada diez veces. En pantalla
            eso no se lee como «segunda opción»: se lee como apagada, y una puerta
            que parece cerrada no se toca. Se viste con el naranja del «Import» de
            la caja de YouTube del consumidor, que resolvió lo mismo hace tiempo.

            LAS CLASES SE COPIAN COMO TEXTO, A PROPÓSITO. El banco del profesor y
            el Panel del consumidor son tuberías separadas y no comparten ni una
            pieza: un import ataría dos superficies con dueños distintos, y el día
            que una retoque su naranja la otra se movería sin que nadie lo pidiera.
            La copia queda VIGILADA por `GroupBankSection.colores.test.js`, que
            fija las cadenas de los dos lados.

            LA ÚNICA DIFERENCIA, DECLARADA: los 44 pt de área de toque se quedan.
            Este carril es superficie de teléfono y sus controles miden eso; el
            botón del Panel vive en escritorio-primero con la altura de la casa. */}
        {/* INFRA-1 (#353 a) — LA GRABACIÓN TAMBIÉN DICE POR DÓNDE VA.
            Mientras sube, el botón cambia al MISMO texto del documento («Subiendo…
            N %», llaves de #348) y el micrófono deja sitio al girador. Una clase
            de tres horas son minutos de subida: hasta hoy el profesor los pasaba
            mirando un botón apagado sin una sola señal de que algo ocurría. */}
        <Button onClick={elegirAudio} disabled={subiendo || !!subidaDoc || lleno}
          className={BOTON_DE_SUBIDA}
          data-testid="group-bank-audio-btn">
          {subidaAudio
            ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            : <Mic className="w-3.5 h-3.5 mr-1.5" />}
          {subidaAudio ? textoDelBoton(t, subidaAudio.pct) : t('groups.bank.audio.upload_btn')}
        </Button>
        <input ref={audioRef} type="file" className="hidden"
          accept=".mp3,.m4a,.wav,.mp4,.mkv,.mov,.webm,audio/*,video/mp4,video/quicktime,video/x-matroska,video/webm" onChange={subirAudio}
          data-testid="group-bank-audio-input" />
      </div>

      {/* T6 — LA FILA DE YOUTUBE. Es un campo y no un botón porque lo que hace
          falta es PEGAR una liga: un botón que abre un modal para pegar una liga
          es un toque de más en el único camino que ya empieza con el portapapeles
          lleno. Se envía con Enter o con el botón. */}
      {/* TANDA 33b — `noValidate`, Y NO ES UN DETALLE DE ESTILO.
          Con `<input type="url">` dentro de un `<form>`, el navegador valida por
          su cuenta antes de dejar pasar el envío y, si no le gusta, pinta un
          globo propio: «Please enter a URL.» Ese texto lo elige el IDIOMA DEL
          NAVEGADOR —no el de la app— y no hay forma de traducirlo ni de darle
          nuestro tono; a un profesor con la app en español le salía en inglés.
          Apagar el guardián deja que el envío llegue a `subirYoutube`, que sí
          sabe decir lo que pasa en el idioma correcto (y con el toast de #304).
          El `type="url"` SE QUEDA: le da al teléfono el teclado de URL, que es
          gratis y correcto. Lo que se apaga es quién habla, no qué sabe. */}
      {/* ajustes-1-3-0 (Bloque F2, ajuste 17): el renglon de YouTube ya no salta de linea: el
          campo toma lo que sobra y el boton, a su derecha, su ancho natural; por debajo de
          420 px de renglon el boton dice solo "Agregar". */}
      <form ref={renglonDeYoutube} className="flex items-center gap-2" noValidate
        onSubmit={(e) => { e.preventDefault(); subirYoutube(); }}
        data-testid="group-bank-youtube-form">
        {/* TANDA 33 (firma #301) — EL ENVOLTORIO EXISTE PARA EL ICONO.
            Un icono «adentro, a la izquierda» del campo se posiciona en absoluto,
            y eso pide un ancestro posicionado: sin él, el icono se colgaría del
            primer ancestro con posición que encuentre y acabaría en cualquier
            parte de la pantalla. Es la misma forma que usa el campo del
            consumidor. El reparto de ancho que traía el campo se muda aquí TAL
            CUAL —el hijo del flex ahora es este envoltorio, así que el crecer y
            el ancho mínimo le tocan a él— y el campo pide el ancho completo de
            su envoltorio. La fila no cambia de comportamiento: mismo mínimo,
            mismo reparto, mismo salto de renglón cuando no cabe. */}
        <div className="relative flex-1 min-w-0">
          <Video className="w-4 h-4 text-[#FF6B00] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input type="url" value={urlYoutube} inputMode="url"
            onChange={(e) => { setUrlYoutube(e.target.value); }}
            placeholder={t('groups.bank.youtube.placeholder')}
            aria-label={t('groups.bank.youtube.add_btn')}
            className="w-full min-h-[44px] rounded-xl bg-[#0d0d0d] border border-[#262626] pl-9 pr-3 text-sm text-white placeholder:text-[#555] outline-none focus:border-[#FF6B00]"
            data-testid="group-bank-youtube-input" />
        </div>
        {/* El gemelo del botón de arriba: mismo vestido copiado del «Import» del
            consumidor, misma excepción de 44 pt. El porqué completo está en el
            comentario de la grabación. */}
        <Button type="submit"
          disabled={subiendo || !!subidaDoc || lleno || !urlYoutube.trim()}
          className="bg-[#FF6B00] hover:bg-[#E05A00] active:bg-[#E05A00] active:scale-[0.98] active:duration-0 text-white rounded-full px-4 md:px-6 text-xs md:text-sm shrink-0 min-h-[44px]"
          data-testid="group-bank-youtube-btn">
          <Video className="w-3.5 h-3.5 mr-1.5" />
          {t(esAngosto(anchoDelRenglon, UMBRAL_YOUTUBE_CORTO)
            ? 'groups.bank.youtube.add_btn_corto' : 'groups.bank.youtube.add_btn')}
        </Button>
      </form>

      <p className="text-xs text-[#6a6a6a]" data-testid="group-bank-hint">
        {t('groups.bank.hint')}
      </p>

      {/* R-1 (PR #350, #343): aquí vivían los dos recuadros —el ámbar de los
          rechazos de subida y el verde de «Listo desde tu última visita»—. El
          primero es ahora toast (ver `avisar`); el segundo se retiró en R-2 de
          REMATES-350 (#346). Entre el pie de la sección y la lista ya no se
          pinta nada. */}

      {modalIdioma && (
        <GroupBankIdiomaModal idiomas={modalIdioma.langs} cargando={subiendo}
          onElegir={(code) => subirYoutube(modalIdioma.url, code)}
          onCerrar={() => setModalIdioma(null)} />
      )}

      {docs === null ? (
        <p className="text-sm text-[#555]" data-testid="group-bank-loading">{t('common:loading')}</p>
      ) : lista.length === 0 ? (
        <p className="text-sm text-[#555]" data-testid="group-bank-empty">{t('groups.bank.empty')}</p>
      ) : (
        <div className="space-y-2" data-testid="group-bank-list">
          {lista.map(doc => {
            const ocupado = !!trabajando[doc.id];
            // ajustes-1-3-0 (Bloque F4, ajuste 8): la tarjeta ENTERA abre la pantalla de temas
            // (lo que hoy hace el nombre), con cursor de mano, flecha a la derecha, role de
            // boton y teclado; el borde naranja al posarse ya lo traia la capa tocable, y es
            // el mismo tono y grosor del de las tarjetas del alumno. Un documento anterior
            // al flip no tiene pantalla de temas (su nombre es un parrafo) y sigue sin
            // abrirse: sin flecha, sin role y sin cursor.
            const abrible = tienePantallaDeTemas(doc);
            const abrir = () => navigate(`/groups/${groupId}/bank/${doc.id}`);
            return (
              /* T5.2 (#136 §4.2) — LA CARD SE DESPEGA DEL FONDO. Estaba en
                 `#0d0d0d` sobre un fondo `#0e0e0e`: un punto MAS OSCURA que el
                 fondo, o sea hundida, y toda la separacion la hacia el borde de
                 1 px. Con la capa del sistema se eleva de verdad. El
                 `data-testid` y el contenido no se mueven. */
              <div key={doc.id}
                className={`${CAPA} ${CAPA_TOCABLE} p-3 space-y-2${abrible ? ' cursor-pointer' : ''}`}
                role={abrible ? 'button' : undefined}
                tabIndex={abrible ? 0 : undefined}
                onClick={abrible ? abrir : undefined}
                onKeyDown={abrible ? (e) => {
                  if (e.target !== e.currentTarget) return;
                  if (e.key !== 'Enter' && e.key !== ' ') return;
                  e.preventDefault();
                  abrir();
                } : undefined}
                data-testid={`group-bank-doc-${doc.id}`}>
                <div className="flex flex-wrap items-center gap-3">
                  {/* t7-2a (C4): la caja del icono (40 x 40) con TRES iconos: documento, video de
                      YouTube o audio, segun el tipo que el servidor ya manda (`file_type`).
                      Remate 2 (#529): es la caja compartida con el Panel del alumno, con el
                      mismo dibujo y ahora con su rotulo para el lector de pantalla. */}
                  <IconoDeMaterial tipo={tipoDeMaterial(doc)} tamano="banco" t={t}
                    testid={`group-bank-icono-${doc.id}`} />
                  <div className="min-w-0 flex-1">
                  {/* PIPE-INST T4 — EL NOMBRE ES LA PUERTA. Tocar el documento
                      abre su pantalla de temas, que es donde ocurre todo lo demás
                      del carril institucional. Es un `button` y no un `div` con
                      `onClick` para que el teclado y el lector de pantalla lo
                      encuentren, y mide 44 pt como el resto de la tarjeta.
                      Elipsis, patrón de PILOTO-1C: un nombre largo no puede
                      empujar el botón de borrar fuera de la tarjeta. */}
                  {tienePantallaDeTemas(doc) ? (
                    <button type="button"
                      onClick={(e) => { e.stopPropagation(); abrir(); }}
                      className="block w-full truncate text-left min-h-[44px]"
                      data-testid={`group-bank-name-${doc.id}`}>
                      {/* t7-2a (C4): el nombre (15 / 600) y, debajo, su linea; los dos dentro del boton,
                          que conserva sus 44 pt. */}
                      <span className="block truncate text-white hover:underline" style={{ fontSize: 15, fontWeight: 600 }}>
                        {doc.filename}
                      </span>
                      {lineaDelMaterial(t, doc) && (
                        <span className="block truncate" style={{ fontSize: 13, color: '#a0a0a0' }}
                          data-testid={`group-bank-linea-${doc.id}`}>{lineaDelMaterial(t, doc)}</span>
                      )}
                    </button>
                  ) : (
                    /* LOS DOCUMENTOS ANTERIORES AL FLIP NO SE ABREN. No tienen
                       mapa ni temas, así que su pantalla contestaría 404 — y la
                       pantalla pintaría «no se pudo procesar este documento,
                       súbelo de nuevo» sobre un documento que sí se procesó. */
                    <>
                      <p className="text-white truncate"
                        style={{ fontSize: 15, fontWeight: 600 }}
                        data-testid={`group-bank-name-${doc.id}`}>{doc.filename}</p>
                      {lineaDelMaterial(t, doc) && (
                        <p className="truncate" style={{ fontSize: 13, color: '#a0a0a0' }}
                          data-testid={`group-bank-linea-${doc.id}`}>{lineaDelMaterial(t, doc)}</p>
                      )}
                    </>
                  )}
                  </div>
                  <div className={esChipDeFila(doc)
                    ? cn(CONTENEDOR_DE_CHIPS, CONTENEDOR_QUE_CEDE) : CONTENEDOR_DE_CHIPS}>
                    {/* EL CHIP, UNIFICADO (firma #118). Los cuatro copys de estado
                        de PILOTO-2 —dos de ellos con conteos y uno que decía «se
                        generó una parte» sobre documentos que no generaron nada—
                        se sustituyen por tres palabras que dicen la verdad. Es un
                        copy de superficie de profesor: español, sin i18n.

                        T4.5 le pone COLOR, y sólo color: el texto no gana ni un
                        carácter. Hasta aquí los tres estados se pintaban en el
                        mismo gris de pie de página —«Error» incluido—, que es
                        justo el estado que hay que poder encontrar de un vistazo
                        en una lista de veinticinco archivos. La receta
                        (fondo 15% / texto 400 / borde 20%) es la que el resto del
                        producto ya repite. */}
                    {/* EL TAG DE PUBLICACIÓN (C32, T5). Va JUNTO al chip de
                        estado y no en su lugar, porque dicen cosas distintas: el
                        chip habla del ARCHIVO («Listo» = se leyó) y el tag habla
                        de lo que ya salió al alumno.

                        ── T5.3 (#140): SON DOS, Y ANTES ERA UNO QUE MENTÍA ────
                        Hasta T5.2 el tag salía con la PRIMERA publicación y no
                        se iba nunca (se leía del contador de cards). Un archivo
                        de ocho temas con UNO publicado se veía IDÉNTICO a uno
                        con los ocho — y «¿ya terminé con este archivo?» es
                        exactamente la pregunta que esta lista existe para
                        contestar de un vistazo.

                        Los dos estados los deriva el SERVIDOR de los temas
                        visibles del documento (`list_group_bank`), y son
                        excluyentes por construcción.

                        EL AZUL SE QUEDA PARA «Publicado» —el mismo del chip de
                        la pantalla de temas, para que el profesor los lea como
                        la misma cosa— y el parcial va en ÁMBAR: dos tags del
                        mismo color con textos que empiezan igual se leen igual
                        de reojo, que es como se lee una lista de veinticinco
                        archivos. El ámbar es además el color que esta card ya
                        usa para «te falta algo aquí».

                        PRECEDENCIA CON LOS AVISOS DE FALLO: el aviso rojo de
                        «Error» y el ámbar de «Listo pero sin temas» viven en
                        otra línea de la card y MANDAN sobre esto — son estados
                        del archivo, y un documento que no se pudo leer no tiene
                        temas que publicar. */}
                    {doc.pi_publicado ? (
                      <span className={`${CHIP_BASE} bg-sky-500/15 text-sky-300
                        border-sky-500/40`}
                        data-testid={`group-bank-publicado-${doc.id}`}>
                        {t('groups.bank.doc.tag_published')}
                      </span>
                    ) : doc.pi_parcialmente_publicado ? (
                      <span className={`${CHIP_BASE} bg-amber-400/15 text-amber-300
                        border-amber-400/40`}
                        data-testid={`group-bank-parcial-${doc.id}`}>
                        {t('groups.bank.doc.tag_partly_published')}
                      </span>
                    ) : null}
                    <span className={esChipDeFila(doc)
                      ? cn(CHIP_BASE, CHIPS_DOC[doc.status] || CHIPS_DOC.error, CHIP_QUE_PARTE)
                      : `${CHIP_BASE} ${CHIPS_DOC[doc.status] || CHIPS_DOC.error}`}
                      data-testid={`group-bank-chip-${doc.id}`}>
                      {chipDeDocumento(t, doc.status, doc)}
                    </span>
                    <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setConfirmarBorrado(doc); }}
                      disabled={ocupado}
                      className="text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded-xl min-h-[44px] min-w-[44px] text-xs"
                      data-testid={`group-bank-delete-${doc.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                    {abrible && (
                      <ChevronRight className="w-4 h-4 text-[#555] shrink-0" aria-hidden="true"
                        data-testid={`group-bank-flecha-${doc.id}`} />
                    )}
                  </div>
                </div>

                {/* OCR-1 (#314): AQUÍ VIVÍA LA BARRA DE PROGRESO CON SU PORCENTAJE
                    (`group-bank-processing-{id}`). Se retiró entera: el estado real
                    del archivo lo dice el chip de arriba, y una barra calibrada para
                    el pipeline del alumno no medía nada de este carril. Sin hueco:
                    la tarjeta sólo pierde una fila. */}
                {/* EL AVISO EN LA CARD, SIN UN CLIC (T4.5).
                    Los cuatro copys de estado de PILOTO-2 se fueron al chip de
                    arriba (firma #118, hallazgo S13) porque decían cuatro cosas en
                    dos idiomas y una de ellas —«se generó una parte (N preguntas)»—
                    era falsa. Pero el chip solo deja MUDOS dos casos que el
                    profesor necesita entender desde la lista, y los dos terminan
                    en un viaje inútil: el documento que murió (abrirlo no explica
                    nada) y el que quedó «Listo» sin un solo tema generable
                    (abrirlo enseña una pantalla vacía). Se dicen aquí, con los
                    copys que la firma #118 ya tiene, sin icono y con el chip
                    intacto: «Listo» sigue diciendo «Listo», porque el documento
                    SÍ se leyó — lo que no encontró es clase. */}
                {avisoDeCard(t, doc) && (
                  <p className={`text-[11px] ${doc.status === 'error'
                    ? 'text-red-400' : 'text-amber-400'}`}
                    data-testid={`group-bank-aviso-${doc.id}`}>
                    {avisoDeCard(t, doc)}
                  </p>
                )}
                {/* OCR-PROFE-MATERIAL (PR #350, M-4 / #321 profe) — EL AVISO DE
                    LECTURA. La ingesta marca `aviso_lectura` cuando al menos el
                    20 % de las páginas no blancas van a visión (o el Word/PPT
                    trae imágenes que se leen). Se pinta bajo el chip, chico y en
                    el mismo blanco del nombre del archivo, SÓLO mientras el chip
                    diga «Procesando…»: al llegar a «Listo» o «Error» ya no
                    explica nada. */}
                {doc.status === 'processing' && doc.aviso_lectura && (
                  <p className="text-[11px] text-white"
                    data-testid={`group-bank-aviso-lectura-${doc.id}`}>
                    {t('groups.bank.doc.aviso_lectura')}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      </div>
    </div>

      {/* Confirmación de borrado. `fixed` → escribe su propia safe area abajo,
          igual que el modal de «quitar alumno» de esta misma página. */}
      {confirmarBorrado && (
        <div className="fixed top-14 inset-x-0 bottom-0 z-[60] bg-black/80 flex items-center justify-center p-4"
          data-testid="group-bank-delete-modal">
          <div className="relative bg-[#111] border border-[#262626] rounded-[20px] p-6 sm:p-8 pb-[max(1.5rem,env(safe-area-inset-bottom,0px))] max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-lg font-semibold text-white mb-2">{t('groups.bank.delete_title')}</h3>
            <p className="text-sm text-[#a0a0a0] mb-5">{t('groups.bank.delete_body')}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button onClick={() => setConfirmarBorrado(null)}
                className="flex-1 bg-[#FF6B00] hover:bg-[#E05A00] text-white rounded-xl min-h-[44px] text-sm"
                data-testid="group-bank-delete-cancel">
                {t('common:cancel')}
              </Button>
              <Button variant="outline" onClick={borrar}
                disabled={!!trabajando[confirmarBorrado.id]}
                className="flex-1 border-[#333] text-red-400 hover:text-red-300 rounded-xl min-h-[44px] text-sm"
                data-testid="group-bank-delete-confirm">
                {trabajando[confirmarBorrado.id]
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : t('groups.bank.delete_confirm_btn')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
